import type {
  DataGridColumn,
  DataGridGrouping,
  DataGridSummaryItem,
  GroupLevel,
  SummaryAggregate,
} from './types';

/**
 * Internal representation of a group node in the tree. `children` is null on
 * leaf groups (innermost level) — those directly own their `rows`.
 */
export interface GroupNode<T> {
  level: number;
  /** The grouping value at this level, coerced to string. */
  key: string;
  /** Full path from the root group down to this node. */
  path: string[];
  /** All rows covered by this node, including those of descendant groups. */
  rows: T[];
  /** Child groups, or `null` if this is a leaf group at the deepest level. */
  children: GroupNode<T>[] | null;
}

/**
 * Visual row kinds rendered by the grid when grouping is active. The
 * component flattens the tree into this sequence and renders each entry
 * with dedicated styling. The grand-total row is rendered separately in
 * `<tfoot>` so it can stick to the bottom of the scroll container.
 */
export type VisualRow<T> =
  | { kind: 'group-header'; level: number; key: string; path: string[]; rows: T[] }
  | { kind: 'data'; row: T; index: number }
  | { kind: 'subtotal'; level: number; path: string[]; rows: T[] };

const DEFAULT_UNGROUPED_LABEL = 'Non assigné';
const PATH_DELIMITER = '\u0000';

/** Opaque key used for the expanded `Set<string>` — unambiguous by design. */
export function pathKey(path: string[]): string {
  return path.join(PATH_DELIMITER);
}

/** Normalize `groupBy` to an array for unified iteration. */
export function resolveGroupLevels<T>(grouping: DataGridGrouping<T>): GroupLevel<T>[] {
  const g = grouping.groupBy;
  return Array.isArray(g) ? g : [g];
}

/**
 * Read one grouping level's value for a row. Null / undefined / empty string
 * collapse to the fallback label so every row ends up in a bucket.
 */
function readGroupValue<T>(
  level: GroupLevel<T>,
  row: T,
  fallback: string
): string {
  let raw: unknown;
  if (typeof level === 'function') {
    raw = level(row);
  } else {
    raw = (row as Record<string, unknown>)[level as string];
  }
  if (raw === null || raw === undefined || raw === '') return fallback;
  return String(raw);
}

/**
 * Build the full group tree from the (already sorted) data. The tree is
 * deterministic: groups at each level appear in the order of their first
 * occurrence in the input — which means outer sorting still propagates.
 */
export function buildGroupTree<T>(
  data: readonly T[],
  grouping: DataGridGrouping<T>
): GroupNode<T>[] {
  const levels = resolveGroupLevels(grouping);
  const fallback = grouping.ungroupedLabel ?? DEFAULT_UNGROUPED_LABEL;

  const build = (rows: T[], levelIndex: number, parentPath: string[]): GroupNode<T>[] => {
    if (levelIndex >= levels.length) return [];
    const currentLevel = levels[levelIndex];
    const buckets = new Map<string, T[]>();

    for (const row of rows) {
      const key = readGroupValue(currentLevel, row, fallback);
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.push(row);
      } else {
        buckets.set(key, [row]);
      }
    }

    const nodes: GroupNode<T>[] = [];
    for (const [key, bucketRows] of buckets) {
      const path = [...parentPath, key];
      const isLeaf = levelIndex === levels.length - 1;
      nodes.push({
        level: levelIndex,
        key,
        path,
        rows: bucketRows,
        children: isLeaf ? null : build(bucketRows, levelIndex + 1, path),
      });
    }
    return nodes;
  };

  return build([...data], 0, []);
}

/**
 * Flatten the tree into a linear stream of visual rows, honoring the
 * `expanded` state per group path. Emits subtotal rows below each expanded
 * group when `hasSummary` is true. The grand-total row is not part of this
 * stream — the component renders it in `<tfoot>` separately so it can be
 * sticky-positioned at the bottom of the scroll container.
 */
export function buildVisualRows<T>(
  tree: GroupNode<T>[],
  expanded: Set<string>,
  hasSummary: boolean
): VisualRow<T>[] {
  const out: VisualRow<T>[] = [];
  let flatIndex = 0;

  const emit = (node: GroupNode<T>) => {
    out.push({
      kind: 'group-header',
      level: node.level,
      key: node.key,
      path: node.path,
      rows: node.rows,
    });

    const isExpanded = expanded.has(pathKey(node.path));
    if (isExpanded) {
      if (node.children && node.children.length > 0) {
        for (const child of node.children) emit(child);
      } else {
        for (const row of node.rows) {
          out.push({ kind: 'data', row, index: flatIndex++ });
        }
      }
      if (hasSummary) {
        out.push({ kind: 'subtotal', level: node.level, path: node.path, rows: node.rows });
      }
    }
  };

  for (const node of tree) emit(node);

  return out;
}

/** Collect every group path in the tree — used to initialize "expand all". */
export function collectAllPaths<T>(tree: GroupNode<T>[]): string[] {
  const keys: string[] = [];
  const walk = (nodes: GroupNode<T>[]) => {
    for (const node of nodes) {
      keys.push(pathKey(node.path));
      if (node.children) walk(node.children);
    }
  };
  walk(tree);
  return keys;
}

/**
 * Read the numeric value of a row for a given summary item. Priority:
 *   1. explicit `value(row)`
 *   2. the column's accessor when it yields a number
 *   3. undefined → row skipped from aggregation
 */
export function readSummaryValue<T>(
  item: DataGridSummaryItem<T>,
  column: DataGridColumn<T> | undefined,
  row: T
): number | undefined {
  if (item.value) {
    const v = item.value(row);
    return v === null || v === undefined ? undefined : v;
  }
  if (!column) return undefined;
  if (typeof column.accessor === 'function') {
    const v = column.accessor(row);
    return typeof v === 'number' ? v : undefined;
  }
  if (column.accessor !== undefined) {
    const v = (row as Record<string, unknown>)[column.accessor as string];
    return typeof v === 'number' ? v : undefined;
  }
  return undefined;
}

/**
 * Compute the aggregated value for a single summary item against a set of
 * rows. Returns `null` when the computation yields nothing (no rows, no
 * numeric values, or the caller's custom compute returned null).
 */
export function computeSummary<T>(
  item: DataGridSummaryItem<T>,
  column: DataGridColumn<T> | undefined,
  rows: T[]
): number | null {
  const aggregate: SummaryAggregate = item.aggregate ?? 'sum';

  if (aggregate === 'custom') {
    if (!item.compute) return null;
    return item.compute(rows);
  }

  if (aggregate === 'count') {
    return rows.length;
  }

  let sum = 0;
  let count = 0;
  for (const row of rows) {
    const v = readSummaryValue(item, column, row);
    if (v === undefined) continue;
    sum += v;
    count += 1;
  }

  if (count === 0) return null;
  if (aggregate === 'avg') return sum / count;
  return sum; // default: 'sum'
}
